export interface SaleRegistryModel {
    _id: string;
    customer: string;
    customerData: CustomerData;
    sale: string;
    saleData: SaleData;
    saleChannelName: string;
    user: string;
    userData: UserData;
    order: number;
    observations: string;
    status: number;
    paymentMethods: PaymentMethod[];
    returnMethods: any[];
    subtotal: number;
    iva: number;
    useIVA?: boolean;
    igtf: number;
    discount: number;
    extraCharges: number;
    utility: number;
    extraServices: ExtraService[];
    locationProducts: LocationProduct[];
    totalPriceUSD: number;
    totalPriceVES: number;
    exchangeRate: number;
    totalPaidUSD: number;
    returnPaidUSD: number;
    tenantId: string;
    deleted: boolean;
    edited: boolean;
    originalOrder: null;
    createdAt: Date;
    orderStr: string;
    originalOrderStr: string;
    updatedAt: Date;
}

export interface CustomerData {
    _id: string;
    dni: string;
    name: string;
    email: string;
    phone: string;
    address: string;
    number: number;
    tenantId: string;
    status: string;
    totalOrder: number;
    totalValue: number;
    createdAt: Date;
    updatedAt: Date;
}

export interface ExtraService {
    _id: string;
    amount: number;
    amountType: string;
    staffMember: string;
    enabled: boolean;
}

export interface LocationProduct {
    product: string;
    name: string;
    amount: number;
    _id: string;
}

export interface PaymentMethod {
    pmId: string;
    name: string;
    currency: Currency;
    amountUSD: number;
    amountVES: number;
    balance: number;
}

export interface Currency {
    name: string;
    code: string;
}

export interface SaleData {
    _id: string;
    name: string;
    status: string;
    useIVA?: boolean;
    products: any[];
    productVariations: ProductVariation[];
    salesChannel: string;
    locations: string[];
    totalPrice: number;
    exchangeRate: number;
    direct: boolean;
    tenantId: string;
    deleted: boolean;
    edited: boolean;
    user: string;
    registryDate: Date;
    createdAt: Date;
    updatedAt: Date;
    // Resultado de la verificación automática de pago móvil (T9). Se persiste en la Sale y viaja
    // en el snapshot `saleData` del bill.
    bankValidationStatus?: string;
    bankValidationDetails?: BankValidationDetails;
}

export interface BankValidationDetails {
    provider?: string;
    paymentMethodId?: string;
    checkedReference?: string;
    bankOrigin?: string;
    phone?: string;
    amount?: number;
    date?: string;
    message?: string;
    verifiedAt?: Date;
}

export interface ProductVariation {
    product: BillProduct;
    type: string;
    amount: number;
    sellingPrice: number;
    iva?: number;
    variations: Variation[];
    location: string;
    _id: string;
}

export interface BillProduct {
    _id: string;
    name: string;
    category: string;
    items: ProductItem[];
    description: string;
    totalEstimatedCost: number;
    sellingPrice: number;
    referenceId: string;
    referenceType: string;
    tenantId: string;
    updatedAt: Date;
    createdAt: Date;
    SKU: null;
    securityStock: number;
}

export interface ProductItem {
    item: PurpleItem;
    itemId: string;
    itemName: string;
    amount: number;
    unitCost: number;
    totalCost: number;
    _id: string;
}

export interface PurpleItem {
    _id: string;
    name: string;
    category: string;
    description: string;
    items: FluffyItem[];
    units: string;
    amount: number;
    totalPrice: number;
    waste: number;
    tenantId: string;
    sellingPrice: number;
    securityStock: number;
    createdAt: Date;
    updatedAt: Date;
    SKU: null;
    skuVariations: any[];
    variations: any[];
    status: string;
    processed: string;
}

export interface FluffyItem {
    amount: number;
    unitPrice: number;
    totalPrice: number;
    location?: string;
    entryDate: Date;
    expirationDate: null;
    lastUpdate: Date;
    skuVariations: any[];
    variations: any[];
    SKU: null;
    _id: string;
}

export interface Variation {
    itemId: string;
    variation: null;
    _id: string;
}

export interface UserData {
    email: string;
    lastName: string;
    name: string;
    _id: string;
}

/** Número de control forma libre / SENIAT: `00-` + 8 dígitos. */
const FISCAL_CONTROL_PREFIX = '00-';
const FISCAL_CONTROL_DIGITS = 8;

/**
 * Formato de display del nº de control (IE-04). No es N. factura.
 *
 * UNIDIGITAL a veces persiste solo la secuencia (`76`). SMART ya guarda `00-00000076`.
 * No se altera un valor alfanumérico (`C-9`, transactionId): no es secuencia SENIAT.
 */
export function formatFiscalControlNumber(value: string | number | null | undefined): string {
    if (value === null || value === undefined) return '';
    const raw = String(value).trim();
    if (!raw) return '';
    if (/^\d{2}-\d{8}$/.test(raw)) return raw;

    const withoutPrefix = raw.startsWith(FISCAL_CONTROL_PREFIX) ? raw.slice(FISCAL_CONTROL_PREFIX.length) : raw;
    if (!/^\d+$/.test(withoutPrefix)) return raw;

    const seq = Number(withoutPrefix);
    if (!Number.isFinite(seq) || seq < 0) return raw;
    const padded = String(Math.floor(seq)).padStart(FISCAL_CONTROL_DIGITS, '0').slice(-FISCAL_CONTROL_DIGITS);
    return `${FISCAL_CONTROL_PREFIX}${padded}`;
}

/**
 * Número de FACTURA fiscal de un pedido. NO es el número de control: ese se muestra aparte, en el
 * detalle del pedido, rotulado como «Control:».
 *
 * `fiscal.invoice.response` es Mixed en el API, así que SMART no guarda la respuesta con una forma
 * fija: el número puede venir bajo `resp`, `response`, `data.resp`, `data.response` o plano, y en dos
 * grafías (`numerointerno` / `numeroInterno`). Se replica la tolerancia de `extractFiscalIdentifier`
 * del API pero SOLO para el número interno: nunca cae a `numerocontrol`/`controlNumber`.
 *
 * UNIDIGITAL (proveedor activo) persiste el número en `documentNumber`, no en `numerointerno`.
 * En scope `imprFiscal` el emisor es la impresora fiscal y no SMART, así que no hay `numerointerno`;
 * ahí el número de la factura es el `ticketNumber`.
 *
 * UniDigital persiste su número de documento en `fiscal.invoice.documentNumber` desde la emisión
 * (existe aun en `awaiting_control_number`, cuando el número de control todavía no llegó). El
 * candidato va gateado por `provider`: en Smart/imprFiscal `documentNumber` es un correlativo
 * interno reservado por Fina que existe incluso en intentos fallidos y NO debe mostrarse.
 *
 * Con varias series UniDigital el número es correlativo POR SERIE, así que se muestra calificado
 * `Serie-Número` (ej. A-7) para no confundir dos facturas distintas con el mismo número. Solo
 * display: nunca alimenta un payload.
 */
export function resolveFiscalInvoiceNumber(bill: any, fallback: string = '-'): string {
    const invoice = bill?.fiscal?.invoice;
    const response = invoice?.response;
    const envelope = response?.resp || response?.response || response?.data?.resp || response?.data?.response;

    const unidigitalSerie = invoice?.provider === 'UNIDIGITAL' && typeof invoice?.serie === 'string' ? invoice.serie.trim() : '';
    const unidigitalNumber = invoice?.provider === 'UNIDIGITAL' ? invoice?.documentNumber : undefined;
    const unidigitalHasNumber = (typeof unidigitalNumber === 'string' && !!unidigitalNumber.trim()) || (typeof unidigitalNumber === 'number' && Number.isFinite(unidigitalNumber));
    const unidigitalCandidate = unidigitalSerie && unidigitalHasNumber ? `${unidigitalSerie}-${String(unidigitalNumber).trim()}` : unidigitalNumber;

    const candidates = [
        envelope?.numerointerno,
        envelope?.numeroInterno,
        response?.numerointerno,
        response?.numeroInterno,
        response?.data?.numerointerno,
        response?.data?.numeroInterno,
        invoice?.ticketNumber,
        unidigitalCandidate,
        // FINA-3051: respaldo para documentos UniDigital sin `provider`. Va después de
        // `unidigitalCandidate` para no perder el formato `serie-número` de Pedidos.
        invoice?.documentNumber,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
        if (typeof candidate === 'number' && Number.isFinite(candidate)) return String(candidate);
    }

    return fallback;
}

/**
 * Número de CONTROL fiscal de la factura de un pedido (columna «N. Control» y exports).
 * NO es el número de factura: ese lo resuelve `resolveFiscalInvoiceNumber`.
 *
 * UniDigital lo persiste plano (`controlNumberFormatted`/`controlNumber`, asignado en ~5-7 min —
 * ausente mientras el documento está en `awaiting_control_number`). Smart lo guarda dentro del
 * `response` Mixed, así que se replica el mismo walk tolerante de envelope que usa el número de
 * factura, pero SOLO para `numerocontrol`/`numeroControl`.
 */
export function resolveFiscalControlNumber(bill: any, fallback: string = '-'): string {
    const invoice = bill?.fiscal?.invoice;
    const response = invoice?.response;
    const envelope = response?.resp || response?.response || response?.data?.resp || response?.data?.response;

    const candidates = [
        invoice?.controlNumberFormatted,
        invoice?.controlNumber,
        envelope?.numerocontrol,
        envelope?.numeroControl,
        response?.numerocontrol,
        response?.numeroControl,
        response?.data?.numerocontrol,
        response?.data?.numeroControl,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
        if (typeof candidate === 'number' && Number.isFinite(candidate)) return String(candidate);
    }

    return fallback;
}

/**
 * ¿La venta ya tiene un documento fiscal emitido? Espejo del util del API (`fiscal-bill.util.ts`),
 * más el `ticketNumber` de imprFiscal. Detectar factura solo por `controlNumber`/`transactionId`
 * dejaba fuera a UniDigital en `awaiting_control_number` — que YA es un documento fiscal aprobado
 * por la imprenta, solo que su número de control llega minutos después: no debe reemitirse, la
 * venta no puede eliminarse, aplica el gate de IGTF en devoluciones y habilita notas.
 *
 * `status` `failed`/`pending` NO cuentan: un intento fallido debe permitir reintentar.
 */
export function hasFiscalInvoice(bill: any): boolean {
    const invoice = bill?.fiscal?.invoice;
    if (!invoice) return bill?.isFiscalInvoice === true;

    return Boolean(
        invoice.controlNumber ||
            invoice.ticketNumber ||
            invoice.response?.transactionId ||
            invoice.providerDocumentId ||
            invoice.status === 'success' ||
            invoice.status === 'awaiting_control_number' ||
            bill?.isFiscalInvoice === true,
    );
}

export function isFiscalInvoiceAnnulled(invoice: any): boolean {
    if (!invoice) return false;
    if (invoice.annulledAt || invoice.annulledBy) return true;
    const flag = invoice.annulled ?? invoice.anulado;
    return flag === true || flag === 'true';
}

export type CxcFiscalInvoiceDisplay = {
    number: string | null;
    serie?: string;
    controlNumber?: string;
    pdfUrl?: string;
    annulled: boolean;
    creditNotes: string[];
};

function pdfUrlFromEntry(entry: any): string | undefined {
    const r = entry?.response?.resp;
    const pdfUrl = entry?.pdfUrl || r?.url || r?.urlpdf || entry?.response?.url || entry?.response?.urlpdf;
    return typeof pdfUrl === 'string' && pdfUrl ? pdfUrl : undefined;
}

/** Display de CxC: factura vigente, NC asociadas, y anulación (letra FINA-3051). */
export function buildCxcFiscalInvoiceDisplay(bill: any): CxcFiscalInvoiceDisplay | null {
    if (!bill || typeof bill !== 'object' || Object.keys(bill).length === 0) return null;

    const invoice = bill?.fiscal?.invoice;
    const creditNotes = (Array.isArray(bill?.fiscal?.creditNotes) ? bill.fiscal.creditNotes : [])
        .filter((note: any) => note?.status === 'success')
        .map((note: any) => resolveFiscalInvoiceNumber({ fiscal: { invoice: note } }, ''))
        .filter((n: string) => !!n);

    const annulled = isFiscalInvoiceAnnulled(invoice);
    if (annulled) {
        return creditNotes.length ? { number: null, annulled: true, creditNotes } : null;
    }

    if (invoice?.status !== 'success') {
        return creditNotes.length ? { number: null, annulled: false, creditNotes } : null;
    }

    const number = resolveFiscalInvoiceNumber(bill, '') || null;
    if (!number && !creditNotes.length) return null;

    const controlNumber = formatFiscalControlNumber(invoice?.controlNumber);
    return {
        number,
        serie: invoice?.serie || undefined,
        controlNumber: controlNumber || undefined,
        pdfUrl: number ? pdfUrlFromEntry(invoice) : undefined,
        annulled: false,
        creditNotes,
    };
}

/** Números vigentes del grupo (factura y NC) para la columna del listado. */
export function groupedFiscalInvoiceNumbers(pendingAccounts: { bill?: any }[] | null | undefined): string {
    const labels: string[] = [];
    for (const pa of pendingAccounts || []) {
        const display = buildCxcFiscalInvoiceDisplay(pa?.bill);
        if (!display) continue;
        if (display.number) labels.push(display.number);
        for (const nc of display.creditNotes) labels.push(`NC ${nc}`);
    }
    return [...new Set(labels)].join(', ');
}

/**
 * FINA-3315: monto en dólares de un cargo extra de la venta. Los cargos por porcentaje guardan el
 * porcentaje en `amount` (ej. 10), así que se aplica sobre el subtotal, igual que el POS y el detalle
 * del pedido. Los fijos y manuales ya traen el monto (`Bill.extraServices` solo guarda `amount`).
 */
export function resolveExtraServiceAmount(extraService: any, subtotal: number): number {
    const amount = Number(extraService?.amount) || 0;
    if (extraService?.amountType !== 'percentage') return amount;
    return Math.round((amount / 100) * (Number(subtotal) || 0) * 100) / 100;
}
